/**
 * Integrytas Group - Base App (MOBILE)
 * Page: OLH.MANAGER — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/OlhManagerPage.tsx. A DISTINCT
 * presentation (dense card list + facet chips + bottom-sheet detail + edit sheet)
 * over the SAME data/logic layer only:
 *   - reads via apiDb (olh_help_item + olh_help_item_tl), resolving the localized
 *     title/body against the ACTIVE locale (§8, §17.B4 — never a raw id reaches
 *     render; the help-type CHECK-enum is shown as a translated label, the APP
 *     screen_code is resolved to its csm_page_master display name),
 *   - the scalar edits (help type / sort order / active) route through the Common
 *     Routine via routedUpdate — §2 single write path; never a direct apiDb write.
 * It never re-renders the desktop grid; §16. Localized title/body authoring, role
 * targeting and tour-step editing stay on the desktop — the localized content is
 * shown read-only here (mirrors MobileUserEditSheet's read-only roles).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Power, X, SquarePen, BookOpen, MessageSquare, Route } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { fetchAllRows } from '../../lib/fetch_all_rows';
import { useActiveLocale } from '../../lib/use_active_locale';
import { routedUpdate } from '../../lib/routed_writes';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill, type MobileLeadTone } from '../components';
import type { AuthSession } from '../../types/auth';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'OLH.MANAGER';

type HelpType = 'DOC' | 'TOOLTIP' | 'TOUR';

// §17.B11: CHECK-enum labels via a labelled constant + t(), never bare literals.
const HELP_TYPES: { value: HelpType; labelKey: string; fallback: string }[] = [
  { value: 'DOC', labelKey: 'mobile.olh.type.DOC', fallback: 'Documentation' },
  { value: 'TOOLTIP', labelKey: 'mobile.olh.type.TOOLTIP', fallback: 'Tooltip' },
  { value: 'TOUR', labelKey: 'mobile.olh.type.TOUR', fallback: 'Guided Tour' },
];

// Per-type lead accent, for a designed (not generic) card list.
const TYPE_VISUAL: Record<HelpType, { lead: MobileLeadTone }> = {
  DOC: { lead: 'blue' },
  TOOLTIP: { lead: 'green' },
  TOUR: { lead: 'amber' },
};

function HelpTypeIcon({ helpType }: { helpType: HelpType }) {
  if (helpType === 'DOC') return <BookOpen />;
  if (helpType === 'TOOLTIP') return <MessageSquare />;
  return <Route />;
}

interface MobileHelpRow {
  olh_help_item_id: number;
  internal_code: string;
  help_type: HelpType;
  screen_type: 'APP' | 'CMS';
  screen_code: string;
  component_key: string | null;
  field_key: string | null;
  sort_order: number;
  is_active: 'Y' | 'N';
  title: string | null;
  body_html: string | null;
}

type StatusFacet = 'all' | 'active' | 'inactive';
type TypeFacet = 'all' | HelpType;

interface TlRow { locale_code: string; title: string | null; body_html: string | null }
interface PageTlRow { locale_code: string; display_text: string }
type RawHelpRow = Omit<MobileHelpRow, 'title' | 'body_html'> & {
  olh_help_item_tl?: TlRow[];
};
type RawPageRow = { internal_code: string; csm_page_master_tl?: PageTlRow[] };

export function MobileOlhManagerPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const [rows, setRows] = useState<MobileHelpRow[]>([]);
  const [pageNames, setPageNames] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [typeFacet, setTypeFacet] = useState<TypeFacet>('all');
  const [selected, setSelected] = useState<MobileHelpRow | null>(null);
  const [editing, setEditing] = useState<MobileHelpRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const typeLabel = (ht: HelpType): string => {
    const def = HELP_TYPES.find((x) => x.value === ht);
    return def ? t(def.labelKey, def.fallback) : ht;
  };

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('olh_help_item')
        .select('olh_help_item_id, internal_code, help_type, screen_type, screen_code, component_key, field_key, sort_order, is_active, olh_help_item_tl ( locale_code, title, body_html )')
        .order('screen_code')
        .order('sort_order');
      if (e) throw e;

      const list: MobileHelpRow[] = ((data ?? []) as RawHelpRow[]).map((r) => {
        const tls = r.olh_help_item_tl ?? [];
        const exact = tls.find((tl) => tl.locale_code === activeLocale);
        const fb = tls.find((tl) => tl.locale_code === 'EN_US') ?? tls[0];
        const chosen = exact ?? fb;
        return {
          olh_help_item_id: r.olh_help_item_id,
          internal_code: r.internal_code,
          help_type: r.help_type,
          screen_type: r.screen_type,
          screen_code: r.screen_code,
          component_key: r.component_key,
          field_key: r.field_key,
          sort_order: r.sort_order,
          is_active: r.is_active,
          title: chosen?.title ?? null,
          body_html: chosen?.body_html ?? null,
        };
      });
      setRows(list);

      // Resolve APP screen codes to their translated page names (§17.B4 — never
      // render the raw code where a name exists). CMS screen codes are user-defined
      // form identifiers with no csm_page_master row, so they fall back to the code.
      const pages = await fetchAllRows((from, to) => apiDb
        .from('csm_page_master')
        .select('internal_code, csm_page_master_tl ( locale_code, display_text )')
        .order('internal_code').order('page_id').range(from, to));
      const names: Record<string, string> = {};
      for (const p of (pages ?? []) as RawPageRow[]) {
        const tls = p.csm_page_master_tl ?? [];
        const exact = tls.find((tl) => tl.locale_code === activeLocale);
        const fb = tls.find((tl) => tl.locale_code === 'EN_US') ?? tls[0];
        names[p.internal_code] = exact?.display_text ?? fb?.display_text ?? p.internal_code;
      }
      setPageNames(names);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (localized title/body
  // and page names follow it — §8).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  // Resolved screen label: "APP:User List" / "CMS:onboarding_form".
  const screenLabel = (r: MobileHelpRow): string => {
    const resolved = r.screen_type === 'APP'
      ? (pageNames[r.screen_code] ?? r.screen_code /* fk-display-ok: page not yet synced */)
      : r.screen_code /* fk-display-ok: CMS form code is a natural identifier */;
    return `${r.screen_type}:${resolved}`;
  };

  const anchorLabel = (r: MobileHelpRow): string =>
    [r.component_key, r.field_key].filter(Boolean).join('.') || t('mobile.olh.anchor_page', 'Whole page');

  const cardTitle = (r: MobileHelpRow): string => r.title?.trim() || r.internal_code;

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFacet === 'active' && r.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && r.is_active !== 'N') return false;
      if (typeFacet !== 'all' && r.help_type !== typeFacet) return false;
      if (!q) return true;
      return (
        r.internal_code.toLowerCase().includes(q) ||
        (r.title ?? '').toLowerCase().includes(q) ||
        `${r.screen_type}:${r.screen_code}`.toLowerCase().includes(q) ||
        [r.component_key, r.field_key].filter(Boolean).join('.').toLowerCase().includes(q)
      );
    });
  }, [rows, filter, statusFacet, typeFacet]);

  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.olh.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const typeFacets: { key: TypeFacet; label: string }[] = [
    { key: 'all', label: t('mobile.olh.type_all', 'All types') },
    ...HELP_TYPES.map((ht) => ({ key: ht.value as TypeFacet, label: t(ht.labelKey, ht.fallback) })),
  ];

  const toggleActive = async (r: MobileHelpRow) => {
    setActionBusy(true);
    setError(null);
    const next = r.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'olh_help_item', 'olh_help_item_id', r.olh_help_item_id, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.olh.title', 'Online Help')}
        description={t('mobile.olh.description', 'Docs, tooltips and guided tours attached to app and CMS screens.')}
      />

      <MobileStatStrip
        stats={[
          { value: rows.length, label: t('mobile.olh.stat.total', 'Total') },
          { value: rows.filter((r) => r.is_active === 'Y').length, label: t('mobile.olh.stat.active', 'Active') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.olh.search', 'Search help items')}
          aria-label={t('mobile.olh.search', 'Search help items')}
        />
      </div>

      <MobileFilterChips
        chips={statusFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      <MobileFilterChips
        chips={typeFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => typeFacet === v}
        onSelect={(v) => setTypeFacet(v as TypeFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.olh.none', 'No help items match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.olh_help_item_id}
              lead={<HelpTypeIcon helpType={r.help_type} />}
              leadTone={TYPE_VISUAL[r.help_type].lead}
              title={cardTitle(r)}
              subtitle={`${typeLabel(r.help_type)} · ${screenLabel(r)}`}
              trailing={
                <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                  {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.olh.detail', 'Help item detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><HelpTypeIcon helpType={selected.help_type} /></span>
              <span>
                <div className="m-nm">{cardTitle(selected)}</div>
                <div className="m-em">{selected.internal_code}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.olh.status', 'Status')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow label={t('mobile.olh.type_lbl', 'Type')} value={typeLabel(selected.help_type)} />
              <MobileDetailRow label={t('mobile.olh.screen', 'Screen')} value={screenLabel(selected)} />
              <MobileDetailRow label={t('mobile.olh.anchor', 'Anchor')} value={anchorLabel(selected)} />
              <MobileDetailRow label={t('mobile.olh.sort', 'Sort order')} value={selected.sort_order} />
            </MobileContentCard>

            {(selected.title?.trim() || selected.body_html?.trim()) && (
              <div className="m-fieldcard">
                <div className="m-formrow">
                  <label>{t('mobile.olh.content', 'Content ({{locale}})', { locale: activeLocale })}</label>
                  <div className="m-formrow-ro">
                    {selected.body_html?.trim() || selected.title?.trim() || t('common.none', 'None')}
                    <span className="m-formrow-hint"> · {t('mobile.olh.content_desktop', 'edit on desktop')}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className="m-btn m-btn-primary"
                disabled={actionBusy}
                onClick={() => { setEditing(selected); setSelected(null); }}
              >
                <SquarePen size={16} />
                {t('mobile.olh.edit', 'Edit')}
              </button>)}
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-ghost')}
                disabled={actionBusy}
                onClick={() => void toggleActive(selected)}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      <MobileOlhEditSheet
        session={session}
        row={editing}
        typeLabel={typeLabel}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Edit sheet — the master-table scalar flags (Help type + Sort order + Active),
// saved through the Common Routine (routedUpdate) — §2. Localized title/body,
// role targeting and tour steps live off the master row and stay on the desktop,
// so the localized content is shown read-only here (mirrors MobileUserEditSheet).
// ---------------------------------------------------------------------------

interface MobileOlhEditSheetProps {
  session: AuthSession;
  row: MobileHelpRow | null;
  typeLabel: (ht: HelpType) => string;
  onClose: () => void;
  onSaved: () => void;
}

function MobileOlhEditSheet({ session, row, typeLabel, onClose, onSaved }: MobileOlhEditSheetProps) {
  const { t } = useTranslation();
  const [helpType, setHelpType] = useState<HelpType>('TOOLTIP');
  const [sortOrder, setSortOrder] = useState(0);
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever a different row is opened.
  useEffect(() => {
    if (!row) return;
    setHelpType(row.help_type);
    setSortOrder(row.sort_order);
    setActive(row.is_active === 'Y');
    setError(null);
  }, [row]);

  const save = async () => {
    if (!row) return;
    setBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'olh_help_item', 'olh_help_item_id', row.olh_help_item_id, {
      help_type: helpType,
      sort_order: sortOrder,
      is_active: active ? 'Y' : 'N',
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = row !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.olh.edit_sheet', 'Edit help item')}>
        <div className="m-grabber" />
        {row && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><HelpTypeIcon helpType={helpType} /></span>
              <span>
                <div className="m-nm">{t('mobile.olh.edit_title', 'Edit help item')}</div>
                <div className="m-em">{row.internal_code}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label htmlFor="m-olh-type">{t('mobile.olh.type_lbl', 'Help type')}</label>
                <select id="m-olh-type" className="m-sel" value={helpType} onChange={(e) => setHelpType(e.target.value as HelpType)}>
                  {HELP_TYPES.map((ht) => <option key={ht.value} value={ht.value}>{typeLabel(ht.value)}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-olh-sort">{t('mobile.olh.sort', 'Sort order')}</label>
                <input
                  id="m-olh-sort"
                  className="m-inp"
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(Number(e.target.value))}
                />
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
                  <div className="m-formrow-s">{t('mobile.olh.active_hint', 'Shown to end users')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  className={'m-switch ' + (active ? 'm-on' : 'm-off')}
                  onClick={() => setActive((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow">
                <label>{t('mobile.olh.content', 'Content')}</label>
                <div className="m-formrow-ro">
                  {row.title?.trim() || row.body_html?.trim() || t('common.none', 'None')}
                  <span className="m-formrow-hint"> · {t('mobile.olh.content_desktop', 'edit on desktop')}</span>
                </div>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
